import { useContext, useEffect, useMemo, useState } from "react"
import { AppContext } from "../App"
import { callApi, CDN_URL, formatMoney, getInlineLoader } from "../Helpers";
import {
  STR_COULD_NOT_FETCH_DATA,
  STR_CROPS,
  STR_DELIVERY_LOCATION,
  STR_INVALID_LOCATION,
  STR_INVALID_QUANTITY,
  STR_LIVESTOCK,
  STR_OPTIONAL,
  STR_ORDER_NOTES,
  STR_ORDER_QUANTITY,
  STR_OVERALL_COST,
  STR_PACKAGING_COST,
  STR_PLACE_YOUR_ORDER,
  STR_SHIPPING_COST,
  STR_SUBMIT,
  STR_TOTAL_ORDER_PRICE,
  STR_TRANSACTION_FEES,
  STR_ITEM_DESCRIPTION
} from "../Strings";
import NapaAddressPicker from "../ones/NapaAddressPicker";
import ImageSlider from "../ones/ImagesSlider";
import OpSuccessWidget from "../ones/OpSuccessWidget";
import { MdLocalShipping, MdStar, MdVerified, MdShoppingCartCheckout } from "react-icons/md";

export default function ViewItem({ uuid }) {

  const appContext = useContext(AppContext);

  /* Place states here */
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [opSuccessful, setOpSuccessful] = useState(false);

  const [data, setData] = useState(null);
  const [images, setImages] = useState([]);

  const [itemQuantity, setItemQuantity] = useState(1);
  const [deliveryLocation, setDeliveryLocation] = useState({});
  const [notes, setNotes] = useState("");

  const [totalOrderPrice, setTotalOrderPrice] = useState(0);
  const [transactionFees, setTransactionFees] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [packagingCost, setPackagingCost] = useState(0);
  const [overallCost, setOverallCost] = useState(0);

  async function init() {
    setReady(false);
    await getData();
    setReady(true);
  }

  async function getData() {
    await callApi("get-item-data", { itemUuid: uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError(STR_COULD_NOT_FETCH_DATA[appContext.language]);
      }
    })
  }

  function getImages() {
    if (!data) return;
    const imgs = [];
    if (data.img1) imgs.push(CDN_URL + data.img1);
    if (data.img2) imgs.push(CDN_URL + data.img2);
    if (data.img3) imgs.push(CDN_URL + data.img3);
    setImages(imgs);
  }

  function calcTotals() {
    setTotalOrderPrice(Number(itemQuantity) * Number(data.unitPrice || 0));
  }

  function calcOverall() {
    setOverallCost(
      Number(totalOrderPrice) +
      Number(transactionFees) +
      Number(packagingCost) +
      Number(shippingCost)
    );
  }

  async function placeOrder() {
    if (Number(itemQuantity) <= 0 || isNaN(itemQuantity)) {
      return appContext.tellError(STR_INVALID_QUANTITY[appContext.language]);
    }

    if (!deliveryLocation?.region) {
      return appContext.tellError(STR_INVALID_LOCATION[appContext.language]);
    }

    setLoading(true);
    await callApi("create-order", {
      itemUuid: uuid,
      itemQuantity,
      deliveryLocation: JSON.stringify(deliveryLocation),
      notes,
      category: (data && data.category) ? data.category : null, // pass category directly
    }).then(res => {
      if (res.status === 1) {
        setOpSuccessful(true);
        appContext.getMyOrders();
      } else {
        appContext.tellError(res.msg);
      }
    });
    setLoading(false);
  }

  const categoryLabel = useMemo(() => {
    if (!data) return "";
    return (data.category === "crop")
      ? STR_CROPS[appContext.language]
      : STR_LIVESTOCK[appContext.language];
  }, [data, appContext.language]);

  useEffect(() => { init(); }, []);
  useEffect(() => { if (data) getImages(); }, [data]);
  useEffect(() => { if (data) calcTotals(); }, [itemQuantity]);
  useEffect(() => { calcOverall(); }, [totalOrderPrice, transactionFees, packagingCost, shippingCost]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading]);

  if (!ready || !data) {
    return <div style={{ padding: 40 }}>{getInlineLoader()}</div>;
  }

  return (
    <div
      style={{
        paddingBottom: "110px",
        background: "linear-gradient(180deg, rgba(255,59,48,0.05) 0%, rgba(255,255,255,0) 30%)",
      }}
    >
      <div className="container" style={{ paddingTop: "14px" }}>

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "6px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  padding: "6px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  background: "rgba(255,255,255,0.92)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "rgba(0,0,0,0.75)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backdropFilter: "blur(8px)",
                }}
                className="font-semi-bold"
              >
                <MdVerified style={{ fontSize: "16px", color: "var(--primaryColor)" }} />
                {categoryLabel}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "12px",
                  color: "rgba(0,0,0,0.65)",
                }}
                className="font-semi-bold"
              >
                <MdStar style={{ fontSize: "16px", color: "#ff3b30" }} />
                4.8
                <span style={{ opacity: 0.6 }}>•</span>
                <span style={{ opacity: 0.75 }}>Fast replies</span>
              </div>
            </div>

            <h4
              style={{
                fontWeight: 800,
                lineHeight: "1.2",
                margin: 0,
                fontSize: "22px",
              }}
              className="font-bold"
            >
              {data.title}
            </h4>

            <div
              style={{
                marginTop: "8px",
                display: "flex",
                alignItems: "baseline",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: "30px",
                  fontWeight: 900,
                  color: "#ff3b30",
                }}
                className="font-bold"
              >
                {formatMoney(data.unitPrice)}
              </div>

              <div
                style={{
                  fontSize: "13px",
                  color: "rgba(0,0,0,0.60)",
                }}
                className="font-semi-bold"
              >
                TZS / {data.unit || "unit"}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "8px 10px",
              borderRadius: "14px",
              background: "rgba(255,255,255,0.9)",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "rgba(0,0,0,0.75)",
              flexShrink: 0,
            }}
            className="font-semi-bold"
          >
            <MdLocalShipping style={{ fontSize: "18px" }} />
            Delivery
          </div>
        </div>

        {/* Gallery + Info */}
        <div className="row">

          <div className="col-lg-7 mb-3">
            <div
              style={{
                borderRadius: "18px",
                overflow: "hidden",
                boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <ImageSlider images={images} />
            </div>

            <div
              style={{
                marginTop: "12px",
                borderRadius: "18px",
                overflow: "hidden",
                background: "var(--surfaceColor)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
              }}
            >
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <div className="font-bold" style={{ fontSize: "15px" }}>
                  {STR_ITEM_DESCRIPTION[appContext.language]}
                </div>

                <div
                  className="font-semi-bold"
                  style={{
                    fontSize: "12px",
                    color: "rgba(0,0,0,0.55)",
                  }}
                >
                  Verified listing
                </div>
              </div>

              <div style={{ padding: "14px 16px" }}>
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    lineHeight: "1.55",
                    fontSize: "14px",
                    color: "rgba(0,0,0,0.80)",
                  }}
                  className="font-regular"
                >
                  {data.description}
                </pre>
              </div>
            </div>
          </div>

          <div className="col-lg-5 mb-3">

            {opSuccessful ? (
              <div
                style={{
                  borderRadius: "18px",
                  background: "var(--surfaceColor)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                  padding: "16px",
                }}
              >
                <OpSuccessWidget />
              </div>
            ) : (
              <div
                style={{
                  borderRadius: "18px",
                  background: "var(--surfaceColor)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >

                {/* Card header */}
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(255,59,48,0.10)",
                        color: "#ff3b30",
                      }}
                    >
                      <MdShoppingCartCheckout style={{ fontSize: "20px" }} />
                    </div>

                    <div>
                      <div className="font-bold" style={{ fontSize: "15px" }}>
                        {STR_PLACE_YOUR_ORDER[appContext.language]}
                      </div>
                      <div
                        className="font-semi-bold"
                        style={{
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.55)",
                        }}
                      >
                        Secure checkout • Pay later
                      </div>
                    </div>
                  </div>

                  <div
                    className="font-bold"
                    style={{
                      padding: "6px 10px",
                      borderRadius: "999px",
                      background: "linear-gradient(135deg, #ff3b30 0%, #ff8a00 100%)",
                      color: "#fff",
                      fontSize: "12px",
                      boxShadow: "0 10px 22px rgba(255,59,48,0.22)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    HOT DEAL
                  </div>
                </div>

                <div style={{ padding: "14px 16px" }}>

                  {/* Qty */}
                  <div style={{ marginBottom: "12px" }}>
                    <label className="font-semi-bold" style={{ fontSize: "13px" }}>
                      {STR_ORDER_QUANTITY[appContext.language]}
                    </label>

                    <div
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        value={itemQuantity}
                        onChange={e => setItemQuantity(e.target.value)}
                        className="form-control text-end"
                        style={{
                          fontSize: "20px",
                          fontWeight: 800,
                          borderRadius: "14px",
                          padding: "12px 14px",
                          boxShadow: "none",
                          border: "1px solid rgba(0,0,0,0.10)",
                        }}
                      />

                      <div
                        className="font-semi-bold"
                        style={{
                          padding: "10px 12px",
                          borderRadius: "14px",
                          background: "rgba(0,0,0,0.04)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          color: "rgba(0,0,0,0.65)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {data.unit || "unit"}
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  {[
                    [STR_TOTAL_ORDER_PRICE, totalOrderPrice],
                    [STR_TRANSACTION_FEES, transactionFees],
                    [STR_PACKAGING_COST, packagingCost],
                    [STR_SHIPPING_COST, shippingCost],
                  ].map(([label, value], i) => (
                    <div
                      key={i}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "14px",
                        background: "rgba(0,0,0,0.03)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                      }}
                      className="font-semi-bold"
                    >
                      <div style={{ fontSize: "13px", color: "rgba(0,0,0,0.70)" }}>
                        {label[appContext.language]}
                      </div>
                      <div style={{ fontSize: "13px", color: "rgba(0,0,0,0.85)" }}>
                        {formatMoney(value)} TZS
                      </div>
                    </div>
                  ))}

                  <div
                    style={{
                      padding: "12px 12px",
                      borderRadius: "16px",
                      background: "rgba(255,59,48,0.08)",
                      border: "1px solid rgba(255,59,48,0.18)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                    className="font-bold"
                  >
                    <div style={{ fontSize: "14px" }}>
                      {STR_OVERALL_COST[appContext.language]}
                    </div>
                    <div style={{ fontSize: "18px", color: "#ff3b30" }}>
                      {formatMoney(overallCost)} TZS
                    </div>
                  </div>

                  {/* Location */}
                  <div style={{ marginTop: "16px" }}>
                    <label className="font-semi-bold" style={{ fontSize: "13px" }}>
                      {STR_DELIVERY_LOCATION[appContext.language]}
                    </label>
                    <div style={{ marginTop: "8px" }}>
                      <NapaAddressPicker callback={setDeliveryLocation} />
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ marginTop: "14px" }}>
                    <label className="font-semi-bold" style={{ fontSize: "13px" }}>
                      {STR_ORDER_NOTES[appContext.language]}
                    </label>
                    <textarea
                      className="form-control"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      style={{
                        marginTop: "8px",
                        borderRadius: "14px",
                        border: "1px solid rgba(0,0,0,0.10)",
                        padding: "12px",
                        minHeight: "90px",
                      }}
                    />
                    <small className="font-semi-bold" style={{ color: "rgba(0,0,0,0.55)" }}>
                      {STR_OPTIONAL[appContext.language]}
                    </small>
                  </div>

                  {/* Submit */}
                  <div style={{ marginTop: "16px" }}>
                    <button
                      className="btn btn-primary btn-rounded btn-block"
                      style={{
                        padding: "14px",
                        fontWeight: 900,
                        borderRadius: "16px",
                        background: "linear-gradient(135deg, #ff3b30 0%, #ff8a00 100%)",
                        border: "none",
                        boxShadow: "0 14px 30px rgba(255,59,48,0.22)",
                      }}
                      onClick={placeOrder}
                    >
                      {STR_SUBMIT[appContext.language]}
                    </button>

                    <div
                      className="font-semi-bold"
                      style={{
                        marginTop: "10px",
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.55)",
                        textAlign: "center",
                      }}
                    >
                      By placing an order, you agree to our platform terms.
                    </div>
                  </div>

                </div>
              </div>
            )}

          </div>

        </div>

      </div>
    </div>
  )
}
